import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, ExternalLink, FileText, Upload } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  scheduled: "bg-amber-50 text-amber-700 border-amber-200",
  published: "bg-blue-50 text-blue-700 border-blue-200",
  archived: "bg-red-50 text-red-700 border-red-200",
};

export default function AdminArticles() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const nav = useNavigate();

  const load = () => {
    const params = new URLSearchParams();
    if (status !== "all") params.set("status", status);
    if (q) params.set("q", q);
    api.get(`/admin/articles${params.toString() ? "?" + params : ""}`).then((r) => setItems(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const remove = async (slug) => {
    if (!window.confirm(`Delete article '${slug}'? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/articles/${slug}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/admin/articles/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Persist as draft immediately so admin can iterate
      const slug = (r.data.title || "untitled").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 100) + "-" + Date.now().toString(36).slice(-4);
      const created = await api.post("/admin/articles", {
        title: r.data.title,
        slug,
        content_html: r.data.content_html,
        status: "draft",
      });
      toast.success("Draft created from upload");
      nav(`/admin/research/articles/${created.data.slug}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    e.target.value = "";
  };

  return (
    <div data-testid="admin-articles-page">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <div className="eyebrow mb-2">Research Hub</div>
          <h1 className="font-display text-3xl font-semibold text-slate-900 tracking-tight">Articles</h1>
        </div>
        <div className="flex gap-2 items-center">
          <label className="h-10 px-3 rounded-md border border-slate-300 text-sm text-slate-700 hover:border-slate-400 inline-flex items-center gap-1.5 cursor-pointer" data-testid="upload-article-btn">
            <Upload className="w-4 h-4" strokeWidth={1.75}/> Upload (MD/DOCX/PDF/HTML)
            <input type="file" accept=".md,.markdown,.html,.htm,.docx,.pdf" onChange={upload} className="hidden"/>
          </label>
          <Link to="/admin/research/articles/new">
            <Button data-testid="new-article-btn" className="bg-blue-600 hover:bg-blue-700 text-white h-10"><Plus className="w-4 h-4 mr-1"/> New article</Button>
          </Link>
        </div>
      </div>

      <div className="mb-4 flex gap-2 flex-wrap">
        {["all", "draft", "scheduled", "published", "archived"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`filter-${s}`} className={`h-8 px-3 rounded-md text-xs border ${status === s ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}>{s}</button>
        ))}
        <div className="flex-1"/>
        <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 max-w-xs"/>
      </div>

      <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
        {items.length === 0 ? (
          <div className="text-center py-16 text-slate-500 text-sm">
            <FileText className="w-8 h-8 mx-auto text-slate-300 mb-3" strokeWidth={1.25}/>
            No articles yet. Create your first one.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs font-mono uppercase tracking-widest text-slate-500">
              <tr><th className="text-left px-4 py-2">Title</th><th className="text-left px-4 py-2">Status</th><th className="text-left px-4 py-2">Updated</th><th className="text-right px-4 py-2">Views</th><th className="text-right px-4 py-2 w-40">Actions</th></tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.slug} className="border-b border-slate-100 last:border-b-0" data-testid={`article-row-${a.slug}`}>
                  <td className="px-4 py-3">
                    <div className="text-slate-900">{a.title}</div>
                    <div className="text-xs text-slate-500 font-mono">{a.slug}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={`rounded-md font-mono text-[10px] uppercase tracking-widest border ${STATUS_STYLES[a.status] || ""}`}>{a.status}</Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{a.updated_at ? new Date(a.updated_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs">{a.views || 0}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {a.status === "published" && (
                      <a href={`/research/${a.slug}`} target="_blank" rel="noreferrer" className="inline-block p-1.5 rounded hover:bg-slate-100"><ExternalLink className="w-3.5 h-3.5 text-slate-500"/></a>
                    )}
                    <Link to={`/admin/research/articles/${a.slug}`} className="inline-block p-1.5 rounded hover:bg-slate-100" data-testid={`edit-${a.slug}`}><Pencil className="w-3.5 h-3.5 text-slate-500"/></Link>
                    <button onClick={() => remove(a.slug)} className="p-1.5 rounded hover:bg-red-50" data-testid={`delete-${a.slug}`}><Trash2 className="w-3.5 h-3.5 text-red-500"/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
